const useUpdateClientProfileForm = () => {
  const { $i18n } = useNuxtApp();
  const { client } = useClientStore();
  const needReload = ref(false);

  const { defineField, errors, handleSubmit, isSubmitting, meta, resetForm, setErrors, setFieldTouched, setValues } =
    useForm<UpdateClientProfileForm>({
      initialValues: {
        country_id: client.value?.country?.id,
        language_id: client.value?.language?.id,
        name: client.value?.name,
        phone: client.value?.phone ? `+${client.value.phone}` : null,
        surname: client.value?.surname,
        timezone: client.value?.timezone,
        priority_project_id: client.value?.priority_project_id,
      },
      validationSchema: toTypedSchema(UPDATE_CLIENT_PROFILE_SCHEMA),
    });

  const fields: UpdateClientProfileFields = {
    country_id: defineField("country_id"),
    form_avatar_files: defineField("form_avatar_files"),
    language_id: defineField("language_id"),
    name: defineField("name"),
    phone: defineField("phone"),
    surname: defineField("surname"),
    timezone: defineField("timezone"),
    priority_project_id: defineField("priority_project_id"),
  };

  const { openNotification } = useNotificationStore();
  const { loading, mutate, onDone, onError } = useUpdateClientProfileDto();
  const loadingAvatar = ref(true);
  const loadingFile = ref(false);

  const loadingForm = computed(() => {
    return loading.value || loadingFile.value || loadingAvatar.value;
  });

  const submit = handleSubmit(async (formData) => {
    if (formData.phone && formData.phone.replaceAll(/\D/g, "").length <= 4) {
      formData.phone = "";
    }

    if (
      formData.form_avatar_files &&
      formData.form_avatar_files[0].name === client.value?.avatar?.name &&
      formData.form_avatar_files[0].size === Number(client.value.avatar?.size)
    ) {
      delete formData.form_avatar_files;
    } else {
      if (client.value?.avatar) {
        if (formData.image_deleted && Array.isArray(formData.image_deleted)) {
          formData.image_deleted.push(client.value.avatar.hash);
        } else {
          formData.image_deleted = [client.value.avatar.hash];
        }
      }

      if (formData.form_avatar_files) {
        loadingFile.value = true;

        const { body, success } = await useUploadFileDto(formData.form_avatar_files);

        loadingFile.value = false;

        if (body.data?.files) {
          const [file] = body.data.files;

          delete formData.avatar;
          formData.avatar = file;
        }

        if (!success) {
          openNotification(localeRuErrors.fileUpload);
        }
      }

      delete formData.form_avatar_files;
    }

    if (formData.language_id !== client.value.language?.id) {
      needReload.value = true;
    }

    await mutate(formData);
  });

  const navigateToNewLanguage = (langCode: string) => {
    const route = useRoute();
    const currentPath = route.fullPath;
    const supportedLocales = $i18n.locales.value?.map((locale) => locale.code) || [];
    const hasLocalePrefix = supportedLocales.some(
      (locale) => currentPath.startsWith(`/${locale}/`) || currentPath === `/${locale}`,
    );
    let newUrl;

    if (hasLocalePrefix) {
      const newPath = supportedLocales.reduce((path, locale) => {
        if (path.startsWith(`/${locale}/`)) {
          return `/${langCode}/${path.slice(locale.length + 2)}`;
        }
        if (path === `/${locale}`) {
          return `/${langCode}`;
        }
        return path;
      }, currentPath);
      newUrl = `${window.location.origin}${newPath}`;
    } else {
      newUrl = `${window.location.origin}/${langCode}${currentPath}`;
    }

    window.location.href = newUrl;
  };

  onDone(async (data) => {
    openNotification($i18n.t("notifications.profileUpdate"));
    const { resolveClient } = useApolloClient();
    const langCode = data?.data?.clientUpdateProfile?.language?.abbr_front ?? "ru";
    resetMeta(meta);

    if (needReload.value) {
      await $i18n.setLocale(langCode);
      const apolloClient = resolveClient();
      await apolloClient.clearStore();

      navigateToNewLanguage(langCode);
      return;
    }

    await useRefetchClient();

    const currentAvatar = fields.form_avatar_files[0].value;

    resetForm({
      values: {
        country_id: client.value?.country?.id,
        language_id: client.value?.language?.id,
        name: client.value?.name,
        phone: client.value?.phone ? `+${client.value.phone}` : null,
        surname: client.value?.surname,
        timezone: client.value?.timezone,
        priority_project_id: client.value?.priority_project_id,
      },
    });

    if (currentAvatar) {
      setValues({
        form_avatar_files: currentAvatar,
      });
    }
  });

  onError(({ graphQLErrors }) => {
    if (graphQLErrors[0]?.extensions.fields) {
      setErrorsFields.graphql(setErrors, onError);
    } else {
      openNotification(graphQLErrors[0]?.message ?? localeRuErrors.internal, {
        type: "error",
      });
    }
  });

  onMounted(async () => {
    if (client.value?.avatar) {
      const file = await useCreateFile(client.value.avatar.url, client.value.avatar.name);

      if (file) {
        const fileList = createFileList([file]);

        setValues({
          form_avatar_files: fileList,
        });
      }
    }

    loadingAvatar.value = false;
  });

  return {
    errors,
    fields,
    handleSubmit: submit,
    isSubmitting,
    loading: loadingForm,
    loadingAvatar,
    meta,
    setFieldTouched,
  };
};

export { useUpdateClientProfileForm };
